import React, { useState } from 'react';
import {
  Button,
  ButtonColor,
} from '../../../../components/uikit/buttons/Button.tsx';
import { CustomModal } from 'components/uikit/modal/CustomModal.tsx';
import { useScopedTranslation } from '../../../../application/localization/useScopedTranslation.ts';
import styles from '../DetailedRepairs.module.scss';
import { Links } from '../../../../application/constants/links.ts';
import {
  RepairsOrderClient,
  SalesPrintDto,
  SalesViewDto,
  UserQuery,
} from 'services/api/api-client.ts';
import clsx from 'clsx';
import { Loading } from '../../../../components/uikit/suspense/Loading.tsx';
import OpenPage from 'assets/icons/open-page.svg?react';
import { ActionText } from '../../../../components/uikit/ActionText.tsx';
import ApprovedIcon from 'assets/icons/success.svg?react';
import { LottieContainer } from '../../../../components/animations/LottieContainer.tsx';
import animationData from '../../../../assets/animations/loader_small.json';
import InfoIcon from 'assets/icons/info-page.svg?react';
import Placeholder from 'assets/img/notifications-placeholder.png';

type OwnProps = {
  onClose: () => void;
  isApproveCostEstimateEnabled: boolean;
  isCostEstimateApproved: boolean;
  isCostEstimateSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
  setIsClicked: (value: boolean) => void;
  onCostEstimateApprove: () => Promise<void>;
  isDataLoading: boolean;
  isFileLoading: boolean;
  isOpenFileClicked: boolean;
  file?: SalesPrintDto;
  data?: SalesViewDto;
};

const email = 'Hamburg.Customer-Care@loewensteinmedical.com';

export const EstimateModal = (props: OwnProps) => {
  const i18n = useScopedTranslation('Page.DetailedRepairs');
  const routeParams = Links.Authorized.DetailedRepairs.useParams();

  const [isApproveModalShown, setIsApproveModalShown] = useState(false);

  const {
    onClose,
    isApproveCostEstimateEnabled,
    isCostEstimateApproved,
    onCostEstimateApprove,
    isDataLoading,
    data,
    isCostEstimateSubmitting,
    setIsSubmitting,
    file,
    isFileLoading,
    isOpenFileClicked,
    setIsClicked,
  } = props;

  const handleOpenFullCostEstimate = () => {
    if (!isOpenFileClicked && isFileLoading) {
      setIsClicked(true);
      return;
    }

    if (file?.href) {
      window.open(file.href, '_blank');
    }
  };

  const handleApprove = async () => {
    try {
      setIsApproveModalShown(false);
      setIsSubmitting(true);
      await RepairsOrderClient.approveCostEstimate(routeParams.incomingNumber);
      await onCostEstimateApprove();
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const userQuery = UserQuery.useGetCurrentUserInfoQuery();

  const tax = (data?.grossTotalSum ?? 0) - (data?.netTotalSum ?? 0);
  const taxFixed = tax === 0 ? 0 : tax.toFixed(2);
  const taxInPercent =
    Math.round((+taxFixed / (data?.netTotalSum ?? 1)) * 100) || 0;

  const netFormatted = `€ ${String(data?.netTotalSum).replace('.', ',')}`;
  const taxFormatted = `€ ${String(taxFixed).replace('.', ',')}`;
  const grossFormatted = `€ ${String(data?.grossTotalSum).replace('.', ',')}`;

  const sendMail = () => {
    const subject = `${i18n.t('discuss_cost_estimate')}${
      routeParams.incomingNumber
    } ${userQuery.data?.userName ?? userQuery.data?.id}`;

    const body = `${i18n.t('discuss_cost_estimate_body')}${
      routeParams.incomingNumber
    } \n${i18n.t('clarify_points')}`;

    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);

    window.location.href = `mailto:${email}?subject=${encodedSubject}&body=${encodedBody}`;
  };

  const titleAdditionalContent =
    data && isCostEstimateApproved ? (
      <div
        className={clsx(
          styles.rowContainer,
          styles.columnGap4,
          styles.approvedContainer,
        )}
      >
        <ApprovedIcon className={styles.approvedIcon} />
        <div className={clsx(styles.text, styles.greenText)}>
          {i18n.t('approved')}
        </div>
      </div>
    ) : undefined;

  const isFetchFailed = !isDataLoading && !data;
  const isOpenFileLoaderShown = isOpenFileClicked && isFileLoading;

  return (
    <CustomModal
      isOpen={true}
      onSubmit={() => setIsApproveModalShown(true)}
      onCancel={sendMail}
      onClose={onClose}
      cancelButtonText={i18n.t('discuss')}
      submitButtonText={i18n.t('approve')}
      submitButtonType={ButtonColor.Primary}
      cancelButtonType={ButtonColor.Secondary}
      titleText={i18n.t('cost_estimate')}
      containerClassName={clsx(
        styles.costEstimateModal,
        isFetchFailed && styles.costEstimateModalEmptyData,
      )}
      hideButtons={true}
      titleAdditionalContent={titleAdditionalContent}
      doNotCloseOnOutsideClick={true}
      contentClassName={!isFetchFailed && styles.paddingRight0}
      buttonContainerClassName={styles.marginRight24}
      textClassName={clsx(
        styles.flex1,
        styles.displayFlex,
        isFetchFailed && styles.placeholderContainer,
      )}
    >
      {isDataLoading && (
        <Loading
          loading={isDataLoading}
          wrapperClassName={styles.loader}
          className={styles.absoluteContainer}
        />
      )}
      {!isFetchFailed ? (
        <div
          className={clsx(
            styles.columnContainer,
            styles.spaceBetween,
            styles.flex1,
          )}
        >
          <div className={clsx(styles.columnContainer, styles.tableContainer)}>
            <div className={clsx(styles.rowContainer, styles.tableHeader)}>
              <div className={styles.headerProductDescription}>
                {i18n.t('description')}
              </div>
              <div className={styles.headerCell}>{i18n.t('quantity')}</div>
              <div className={styles.headerCell}>{i18n.t('unit')}</div>
              <div className={styles.headerCell}>{i18n.t('price')}</div>
              <div className={styles.headerCell}>{i18n.t('discount')}</div>
              <div className={styles.headerCell}>{i18n.t('total')}</div>
            </div>
            <div className={styles.overflowContainer}>
              {data?.items?.map((item) => {
                return (
                  <div
                    key={item.article}
                    className={clsx(styles.rowContainer, styles.borderBottom)}
                  >
                    <div className={styles.productDescription}>
                      <div className={styles.bold}>{`WM ${item.article}`}</div>
                      {item.productDescription && (
                        <div>{`${item.productDescription}`}</div>
                      )}
                    </div>
                    <div className={styles.cell}>{item.unitOfSaleCount}</div>
                    <div className={styles.cell}>{item.unitOfSaleType}</div>
                    <div className={styles.cell}>{`€ ${String(
                      item.initialPrice,
                    ).replace('.', ',')}`}</div>
                    <div className={styles.cell}>
                      {`${String(item.discountInPercent).replace('-', '')}%`}
                    </div>
                    <div className={styles.cell}>{`€ ${String(
                      item.totalPrice,
                    ).replace('.', ',')}`}</div>
                  </div>
                );
              })}
            </div>
            {!isDataLoading && (
              <div className={styles.totalContainer}>
                <div className={clsx(styles.bold)}>{i18n.t('total_price')}</div>
                <div className={styles.totalPriceContainer}>
                  <div className={clsx(styles.columnContainer, styles.rowGap4)}>
                    <div className={styles.greyText}>{i18n.t('net_total')}</div>
                    <div className={styles.text}>{netFormatted}</div>
                  </div>
                  <div className={styles.totalDivider} />
                  <div className={clsx(styles.columnContainer, styles.rowGap4)}>
                    <div
                      className={clsx(
                        styles.greyText,
                        styles.rowContainer,
                        styles.columnGap4,
                      )}
                    >
                      <div>{i18n.t('tax')}</div>
                      <div>{taxInPercent}%</div>
                    </div>
                    <div className={styles.text}>{taxFormatted}</div>
                  </div>
                  <div className={styles.totalDivider} />
                  <div className={clsx(styles.columnContainer, styles.rowGap4)}>
                    <div className={styles.greyText}>
                      {i18n.t('total_with_tax')}
                    </div>
                    <div className={styles.bold}>{grossFormatted}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className={clsx(styles.buttonContainer)}>
            <div>
              {!isDataLoading && !isOpenFileLoaderShown && (
                <ActionText
                  text={i18n.t('open_full_cost_estimate')}
                  Svg={OpenPage}
                  onClickHandler={handleOpenFullCostEstimate}
                />
              )}
              {isOpenFileLoaderShown && (
                <LottieContainer
                  animationData={animationData}
                  segments={isOpenFileLoaderShown ? [1, 180] : [180, 270]}
                  loop={isOpenFileLoaderShown}
                  width={28}
                  height={28}
                  className={styles.lottieAnimation}
                />
              )}
            </div>
            {!isCostEstimateApproved && isApproveCostEstimateEnabled && (
              <div
                className={clsx(
                  styles.rowContainer,
                  styles.columnGap14,
                  styles.marginRight24,
                )}
              >
                <Button
                  title={i18n.t('discuss')}
                  color={ButtonColor.Secondary}
                  onClick={sendMail}
                />
                <Button
                  title={i18n.t('approve')}
                  testId="approve-cost-estimate"
                  color={ButtonColor.Primary}
                  onClick={() => setIsApproveModalShown(true)}
                  loading={isCostEstimateSubmitting}
                  disabled={isCostEstimateSubmitting}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div>
          <img className={styles.placeholderImage} src={Placeholder} />
          <div className={clsx(styles.placeholderTitle)}>
            {i18n.t('estimate_placeholder_title')}
          </div>
          <div className={clsx(styles.text, styles.textAlignCenter)}>
            {i18n.t('estimate_placeholder_text')}
          </div>
        </div>
      )}

      {isApproveModalShown && (
        <CustomModal
          isOpen={true}
          onCancel={() => setIsApproveModalShown(false)}
          onSubmit={handleApprove}
          submitButtonText={i18n.t('approve')}
          submitButtonTestId="confirm-approve-cost-estimate"
          cancelButtonText={i18n.t('cancel')}
          titleText={i18n.t('approving_cost_estimate')}
          submitButtonType={ButtonColor.Primary}
          cancelButtonType={ButtonColor.Secondary}
          containerClassName={styles.approveCostEstimateModal}
          textClassName={styles.lineHeight24}
        >
          <div>
            <div
              className={clsx(
                styles.rowContainer,
                styles.columnGap8,
                styles.modalInfoContainer,
              )}
            >
              <div className={styles.marginTop3}>
                <InfoIcon />
              </div>
              <div className={clsx(styles.text, styles.lineHeight24)}>
                {i18n.t('approving_cost_estimate_body_text')}
              </div>
            </div>
            <div className={clsx(styles.greyModalText, styles.lineHeight24)}>
              {i18n.t('approving_cost_estimate_body_text_two')}
            </div>
          </div>
        </CustomModal>
      )}
    </CustomModal>
  );
};
